<template>
  <div id="main" :class="`page-${languageCode}`">
    <router-view v-if="routerAlive"/>
    <RectLoading v-show="Boolean(showLoading)"/>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import VConsole from 'vconsole'
import { ACCOUNT_TYPE, NEED_MANUALLY_CLOSE_ERROR_CODE, NO_MESSAGE_ERROR_CODE } from './constant/common'
import RectLoading from './components/loading/rectLoading'

export default {
  components: {
    RectLoading
  },
  metaInfo () {
    return {
      title: this.pageTitle,
      link: [{
        vmid: 'favicon',
        rel: 'icon',
        href: this.faviconUrl
      }],
      style: [{
        innerHTML: this.fontStyle.fontStyle
      }]
    }
  },
  data () {
    return {
      routerAlive: true,
      showLoading: 0, // (any == 0) = false; (any != 0) = false
      customTitle: null,
      openVConsole: false,
      companyId: null
    }
  },
  provide () {
    const vm = this
    return {
      getCustomFontFromTheme: vm.getCustomFontFromTheme
    }
  },
  computed: {
    ...mapGetters('company', ['companyThemeSetting']),
    ...mapGetters('account', ['isCompanyAccount', 'isHeadhunterAccount']),
    languageCode () {
      return this.$i18n.locale === 'zh-CN' || this.$i18n.locale === 'zh' ? 'zh-cn' : 'en'
    },
    pageTitle () {
      if (this.customTitle) {
        return this.customTitle
      } else {
        const meta = this.$route.meta
        if (meta) {
          if (meta.titleI18nKey && this.$te(meta.titleI18nKey)) {
            return this.$t(meta.titleI18nKey)
          }
          return meta.title
        }
      }
      return ''
    },
    faviconUrl () {
      return this.companyThemeSetting?.commonSetting?.faviconImage?.url || '/static/images_en/ajinga_logo.png'
    },
    fontStyle () {
      const commonSetting = this.companyThemeSetting?.commonSetting || {}
      const fontFamilyList = commonSetting.customFont?.fontFamilyList || []
      const fontFamily = _.chain(fontFamilyList).filter(item => item.active).map(item => `"${item.name}"`).value()
      const fontFace = _.chain(fontFamilyList).filter(item => item.active && !item.isDefault).map(item => {
        const urls = _.map(item.fileList, file => {
          return `url('${file.url}') format('${file.ext}')`
        }).join(',\n')

        return `@font-face {
          font-family: "${item.name}";
          src: 
            ${urls};
          font-weight: normal;
          font-style: normal;
        }`
      }).value().join('\n')
      return fontFamily && fontFamily.length > 0
        ? {
            fontStyle: `
          ${fontFace}
          html, body, #main {
            font-family: ${fontFamily.join(',')};
          }
        `,
            fontFamily: fontFamily
          }
        : {}
    }
  },
  async created () {
    this.handleEventFromBus()
  },
  methods: {
    ...mapActions('account', ['clearAccountInfo']),
    ...mapActions('hm', ['cleanHMToken']),
    reloadPage () {
      this.routerAlive = false
      this.$nextTick(() => {
        this.routerAlive = true
      })
    },
    handleEventFromBus () {
      this.$eventBus.$on('setCustomTitle', (title) => {
        this.customTitle = title
      })
      this.$eventBus.$on('reloadPage', () => {
        // eslint-disable-next-line no-console
        console.warn(`reload page(${this.$route.path}) has disabled by code`)
        // to-do refine reload page, this will clear all data(include api data)
        // this.routerAlive = false
        // this.$nextTick(() => {
        //   this.routerAlive = true
        // })
      })
      this.$eventBus.$on('showLoading', this.showMaskLoading)
      this.$eventBus.$on('responseErrorCode', this.handleResponseErrorCode)
      this.$eventBus.$on('responseHttpError', this.handleResponseHttpError)
      if (this.$eventBus.nativeShowLoading) {
        this.showMaskLoading(true)
      }
    },
    showMaskLoading (isShow, ignoreOpenCount) {
      if (ignoreOpenCount) {
        if (isShow) {
          this.showLoading = 1
        } else {
          this.showLoading = 0
        }
      } else {
        if (isShow) {
          this.showLoading++
        } else {
          this.showLoading--
        }
        if (this.showLoading < 0) {
          this.showLoading = 0
        }
      }
    },
    handleResponseErrorCode (errorCode, resp) {
      // auto skip handle when this code has no i18n message
      // NO_MESSAGE_ERROR_CODE will skip handle even this code has i18n message
      if (NO_MESSAGE_ERROR_CODE.includes(errorCode)) {
        return
      }
      // 当直接在前端 url 中带 token 参数去免密登录，如果没有密码应该跳转到创建密码, 考虑影响大, 暂时不打开
      // if (errorCode === ACCOUNT_NEED_CREATE_PASSWORD_ERROR_CODE && resp.data.password_reset_code) {
      //   this.$router.push({ path: `/passport/candidate/create-password/${resp.data.password_reset_code}`, query: { back: resp.data.redirect_url } })
      //   this.$eventBus.nativeShowLoading = false
      //   return
      // }
      const errorI18nKey = `error.code.${errorCode}`
      if (this.$te(errorI18nKey)) {
        if (NEED_MANUALLY_CLOSE_ERROR_CODE.includes(errorCode)) {
          this.$message({
            duration: 0,
            showClose: true,
            message: this.$t(errorI18nKey),
            type: 'error'
          })
        } else {
          this.$message.error(this.$t(errorI18nKey))
        }
      }
      if (process.env.NODE_ENV !== 'production') {
        if (!this.$te(errorI18nKey)) {
          // eslint-disable-next-line no-console
          console.warn(`no message found, auto skip handle error code: ${errorCode}`)
        }
      }
    },
    async handleResponseHttpError (statusCode, error) {
      const currentPath = this.$route.path
      let accountType = ACCOUNT_TYPE.CANDIDATE
      const query = {
        ...this.$route.query,
        back: this.$route.fullPath
      }
      switch (statusCode) {
        case 401:
          await this.clearAccountInfo()
          if (currentPath.startsWith('/onboarding/')) {
            query.onboarding_id = this.$route.params.invitationId
          } else if (currentPath.startsWith('/recruiting/company/')) {
            query.company_id = this.$route.params.companyId
          } else if (currentPath.startsWith('/v-admin/hm/')) {
            await this.cleanHMToken()
            accountType = ACCOUNT_TYPE.HIRING_MANAGER
          } else if (currentPath === '/passport/hiring-manager/login' || currentPath === '/passport/hiring-manager/login/') {
            await this.cleanHMToken()
            break
          } else if (currentPath.startsWith('/passport/hiring-manager')) {
            await this.cleanHMToken()
            accountType = ACCOUNT_TYPE.HIRING_MANAGER
          } else if (currentPath.startsWith('/v-admin/')) {
            accountType = ACCOUNT_TYPE.HR
          }
          this.$router.push({ path: `/passport/${accountType}/${accountType === ACCOUNT_TYPE.CANDIDATE ? 'register' : 'login'}`, query })
          break
        case 403:
          this.$router.replace('/error/no-permission')
          break
        case 404: {
          const requestUrl = error.config.url
          if (process.env.NODE_ENV !== 'production' && process.env.VUE_APP_ENV === 'development') {
            this.$message.error(`${statusCode} at: ${error.config.url}`)
          } else if (!requestUrl.includes('http') || requestUrl.startsWith(location.origin)) {
            this.$router.replace('/error/404.html')
          }
          break
        }
        case 500:
          this.$message.error(this.$t('error.message.serverError'))
          break
        case 503:
          this.$router.push(`/error/503.html?back=${this.$route.path}`)
          break
      }
    },
    getCustomFontFromTheme () {
      return this.fontStyle?.fontFamily?.join(',')
    }
  },
  watch: {
    $route (to) {
      const query = to.query
      if (query.$force_vconsole === '1') {
        this.openVConsole = true
      }
    },
    async openVConsole (newVal) {
      if (newVal) {
        this.vConsole = new VConsole()
      }
    }
  }
}
</script>

<style lang="scss">
html,
body {
  font-family:"PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", 微软雅黑, Arial, sans-serif;
}

#main {
  position: relative;

  .loading-mask {
    z-index: 10000;
    display: flex;

    .van-loading {
      margin: auto;

      .van-loading__spinner--spinner {
        i::before {
          width: 5px;
        }
      }
    }
  }
}

.el-tooltip__popper {
  max-width: 600px;
}

.van-toast.van-toast--text {
  word-break: break-word;
}
</style>
